import React, {useEffect, useState} from 'react';
import {API_CONFIG, getHeaders} from '../../config/api';
import {channelService, formatWalletBalanceMinor} from '../../services/channelService';
import {internalApi, getApiErrorMessage, useApiSessionRestored} from '../../services/internalApi';


function apiErr(e) {
    return getApiErrorMessage(e, 'Request failed');
}

const getAvailablePartners = async () => {
    const response = await internalApi.get(API_CONFIG.ENDPOINTS.GET_PARTNERS);

    if (response.data && response.data.results) {
        return response.data.results.map(partner => ({
            id: partner.id,
            label: partner.name
        }));
    }
    return [];
};

function B2BPartnerSection({
                               selectedPartner,
                               onPartnerChange,
                               customPartner,
                               onCustomPartnerChange,
                               pocName,
                               doctor,
                               onPocNameChange,
                               draft,
                               onDraftUpdate
                           }) {
    const [partners, setPartners] = useState([]);
    const [partnersError, setPartnersError] = useState(null);
    const [discountError, setDiscountError] = useState(null);
    const [discountSubmitting, setDiscountSubmitting] = useState(false);
    const [walletInfo, setWalletInfo] = useState(null);
    const [walletLoading, setWalletLoading] = useState(false);
    const [walletError, setWalletError] = useState(null);
    const apiSessionRestored = useApiSessionRestored();

const transformDraftForAPI = (draft) => {
        return {
            ...draft,
            items: draft.items.map(item => ({
                medicine_id: item.medicine.id,
                medicine_name: item.medicine.name,
                manufacturer: item.medicine.manufacturer,
                price: Math.round(item.medicine.price * 100), // Convert to paise
                price_to_retailer: Math.round(item.medicine.price_to_retailer * 100), // Convert to paise
                quantity: item.quantity,
                discount: item.discount, // discount in percentage
                batch: item.medicine.batch,
                expiry: item.medicine.expiry,
                packing_size: item.medicine.packing_size,
                discount_tier: item.medicine.discount_tier,
                size: Number(item.medicine.size),
            })),
            additional_charges: draft.additionalCharges.map(charge => ({
                charge_type: charge.type,
                name: charge.name,
                amount: Math.round(charge.amount * 100) // Convert to paise
            }))
        };
    };

    const transformDraftFromAPI = (draft) => {
        return {
            ...draft,
            items: draft.items.map(item => ({
                medicine: {
                    id: item.medicine_id,
                    name: item.medicine_name,
                    manufacturer: item.manufacturer,
                    content: item.content || '',
                    size: item.size || 1,
                    price: Number(item.price) / 100,
                    price_to_retailer: Number(item.price_to_retailer) / 100,
                    batch: item.batch,
                    expiry: item.expiry,
                    packing_size: item.packing_size,
                    discount_tier: item.discount_tier,
                    stock: item.stock || '-'
                },
                quantity: item.quantity,
                discount: item.discount  // discount in percentage
            })),
            additionalCharges: draft.additional_charges.map(charge => ({
                type: charge.charge_type,
                name: charge.name,
                amount: Number(charge.amount) / 100
            }))
        };
    };

    const handleApplyDiscount = async () => {
        setDiscountError(null);
        setDiscountSubmitting(true);
        try {
            const transformedDraft = transformDraftForAPI(draft);
            const response = await internalApi.post(
                API_CONFIG.ENDPOINTS.APPLY_DISCOUNT(transformedDraft.partner),
                transformedDraft,
                {headers: getHeaders(draft.pharmacy, transformedDraft.partner)}
            );

            if (response.data && response.data.data) {
                const transformedResponse = transformDraftFromAPI(response.data.data);
                onDraftUpdate(transformedResponse);
            }
        } catch (error) {
            console.error('Error applying partner discount:', error);
            setDiscountError(apiErr(error));
        } finally {
            setDiscountSubmitting(false);
        }
    };


    useEffect(() => {
        setPartnersError(null);
        getAvailablePartners()
            .then((fetchedPartners) => setPartners(fetchedPartners))
            .catch((err) => {
                console.error('Error fetching available partners:', err);
                setPartnersError(apiErr(err));
                setPartners([]);
            });
    }, [apiSessionRestored]);

    useEffect(() => {
        setDiscountError(null);
    }, [selectedPartner]);

    useEffect(() => {
        if (!selectedPartner || selectedPartner === 'CUSTOM') {
            setWalletInfo(null);
            setWalletError(null);
            setWalletLoading(false);
            return;
        }

        let cancelled = false;
        setWalletLoading(true);
        setWalletError(null);
        setWalletInfo(null);

        channelService
            .getChannelWallet(selectedPartner)
            .then((data) => {
                if (!cancelled) {
                    setWalletInfo(data);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    console.error('Error fetching partner wallet:', err);
                    setWalletError(getApiErrorMessage(err, 'Could not load wallet'));
                    setWalletInfo(null);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setWalletLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [selectedPartner, apiSessionRestored]);

    return (
        <div className="card-surface mb-6 overflow-hidden border-l-4 border-l-primary-500">
            <div className="bg-gradient-to-r from-slate-50 to-white px-5 py-4">
                <h3 className="text-lg font-semibold text-slate-800">B2B partner</h3>
                <p className="text-sm text-slate-500">Channel pricing and wallet for this draft.</p>
            </div>
            <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-2">
                {partnersError && (
                    <div className="md:col-span-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
                        {partnersError}
                    </div>
                )}
                <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                        Select partner
                    </label>
                    <select
                        className="input-field"
                        value={selectedPartner}
                        onChange={(e) => onPartnerChange(e.target.value)}
                    >
                        <option value="">Select a partner</option>
                        {partners.map(option => (
                            <option key={option.id} value={option.id}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                {selectedPartner && selectedPartner !== 'CUSTOM' && (
                    <div className="md:col-span-2 rounded-xl border border-emerald-100 bg-gradient-to-br from-emerald-50/80 to-white p-4">
                        {walletLoading && (
                            <div className="flex items-center gap-2 text-sm text-slate-600">
                                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent" />
                                Loading wallet…
                            </div>
                        )}
                        {!walletLoading && walletError && (
                            <p className="text-sm text-red-600">{walletError}</p>
                        )}
                        {!walletLoading && !walletError && walletInfo && (
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
                                        <i className="material-icons-outlined">payments</i>
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium uppercase tracking-wide text-emerald-800/80">
                                            Wallet balance
                                        </p>
                                        <p className="text-xl font-bold tabular-nums text-emerald-900">
                                            {formatWalletBalanceMinor(walletInfo.balance)}
                                        </p>
                                    </div>
                                </div>
                                {walletInfo.wallet_id != null && (
                                    <div className="text-right sm:text-left">
                                        <p className="text-xs font-medium text-slate-500">Wallet ID</p>
                                        <code className="mt-0.5 inline-block max-w-full truncate rounded-lg bg-white/80 px-2 py-1 text-xs text-slate-800 ring-1 ring-slate-200/80">
                                            {walletInfo.wallet_id}
                                        </code>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {selectedPartner === 'CUSTOM' && (
                    <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">
                            Custom partner name
                        </label>
                        <input
                            type="text"
                            className="input-field"
                            value={customPartner}
                            onChange={(e) => onCustomPartnerChange(e.target.value)}
                            placeholder="Enter partner name"
                        />
                    </div>
                )}

                <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                        POC name
                    </label>
                    <input
                        type="text"
                        className="input-field"
                        value={pocName}
                        onChange={(e) => onPocNameChange(e.target.value)}
                        placeholder="Enter POC name"
                    />
                </div>

                {selectedPartner && (
                    <div className="md:col-span-2 space-y-2">
                        {discountError && (
                            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
                                {discountError}
                            </p>
                        )}
                        <div className="flex items-end">
                            <button
                                type="button"
                                onClick={handleApplyDiscount}
                                disabled={discountSubmitting}
                                className="btn-primary w-full sm:w-auto sm:min-w-[200px]"
                            >
                                <span className="inline-flex items-center gap-2">
                                    {discountSubmitting ? (
                                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                                    ) : (
                                        <i className="material-icons-outlined text-base">local_offer</i>
                                    )}
                                    {discountSubmitting ? 'Applying…' : 'Apply partner discount'}
                                </span>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default B2BPartnerSection;